import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Send, Phone } from 'lucide-react';
import { servicesAPI, casesAPI, stylistProfileAPI } from '../services/api';
import ContactForm from '../components/ContactForm';

const Home = () => {
  const [services, setServices] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);
  const [aboutStylist, setAboutStylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [servicesData, casesData, stylistData] = await Promise.all([
          servicesAPI.getAll(),
          casesAPI.getAll(),
          stylistProfileAPI.getProfile()
        ]);
        
        setServices(servicesData);
        setCaseStudies(casesData);
        setAboutStylist(stylistData);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Ошибка загрузки данных');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: 'var(--white)'
      }}>
        <p style={{ color: 'var(--charcoal)', fontSize: '1.125rem' }}>Загрузка...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: 'var(--white)'
      }}>
        <p style={{ color: 'var(--charcoal)', fontSize: '1.125rem' }}>{error}</p>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: 'var(--white)' }}>
      {/* Hero Section - Magazine Style */}
      <section style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        paddingTop: 'var(--space-huge)',
        paddingBottom: 'var(--space-xxxl)'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          width: '100%',
          padding: '0 var(--space-xl)',
          display: 'grid',
          gridTemplateColumns: '1.2fr 1fr',
          gap: 'var(--space-xxxl)',
          alignItems: 'center'
        }}
        className="hero-grid">
          {/* Left - Typography */}
          <div style={{ paddingRight: 'var(--space-xxl)' }}>
            <p className="caption" style={{
              color: 'var(--mid-gray)',
              marginBottom: 'var(--space-xl)'
            }}>
              Стилист в Санкт-Петербурге
            </p>
            <h1 style={{
              marginBottom: 'var(--space-xl)',
              color: 'var(--black)',
              lineHeight: '1.05'
            }}>
              СТИЛЬ, В КОТОРОМ ВЫ <span style={{ fontStyle: 'italic' }}>ЧУВСТВУЕТЕ СЕБЯ СОБОЙ</span>
            </h1>
            <div style={{
              width: '60px',
              height: '2px',
              backgroundColor: 'var(--accent-gold)',
              marginBottom: 'var(--space-xl)'
            }} />
            <p className="large-text" style={{
              color: 'var(--charcoal)',
              marginBottom: 'var(--space-xxl)',
              maxWidth: '540px'
            }}>
              Создаю образы, которые соответствуют вашему ритму жизни, настроению и характеру
            </p>
            <button className="btn-primary" onClick={scrollToContact}>
              <span>Записаться на консультацию</span>
            </button>
          </div>

          {/* Right - Large Image */}
          <div style={{
            position: 'relative',
            height: '75vh',
            minHeight: '600px',
            overflow: 'hidden',
            border: '1px solid var(--soft-gray)'
          }}
          className="hero-image">
            <img
              src="https://images.pexels.com/photos/1926769/pexels-photo-1926769.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="Personal Style"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 8s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              className="parallax-image"
            />
          </div>
        </div>
      </section>

      {/* Manifesto Strip */}
      <section style={{
        backgroundColor: 'var(--black)',
        color: 'var(--white)',
        padding: 'var(--space-xl) 0',
        overflow: 'hidden'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          whiteSpace: 'nowrap',
          animation: 'scroll 30s linear infinite'
        }}>
          {[...Array(3)].map((_, i) => (
            <div key={i} style={{
              display: 'flex',
              alignItems: 'center',
              paddingRight: 'var(--space-xxxl)'
            }}>
              <span style={{ 
                fontFamily: 'var(--font-display)',
                fontSize: '1.25rem',
                fontWeight: 300,
                letterSpacing: '0.1em'
              }}>
                Уверенность в каждом образе
              </span>
              <span style={{ margin: '0 var(--space-xl)', color: 'var(--accent-gold)' }}>•</span>
              <span style={{ 
                fontFamily: 'var(--font-display)',
                fontSize: '1.25rem',
                fontWeight: 300,
                letterSpacing: '0.1em'
              }}>
                Ваша индивидуальность — моё вдохновение
              </span>
              <span style={{ margin: '0 var(--space-xl)', color: 'var(--accent-gold)' }}>•</span>
              <span style={{ 
                fontFamily: 'var(--font-display)',
                fontSize: '1.25rem',
                fontWeight: 300,
                letterSpacing: '0.1em'
              }}>
                Стиль, который подчеркивает вашу красоту
              </span>
              <span style={{ margin: '0 var(--space-xl)', color: 'var(--accent-gold)' }}>•</span>
            </div>
          ))}
        </div>
      </section>

      {/* Services - Magazine Grid */}
      <section className="section" style={{ backgroundColor: 'var(--cream)' }}>
        <div className="container">
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            marginBottom: 'var(--space-xxxl)'
          }}
          className="section-header">
            <div>
              <p className="caption" style={{ color: 'var(--mid-gray)', marginBottom: 'var(--space-m)' }}>
                Мои услуги
              </p>
              <h2 style={{ color: 'var(--black)' }}>Как я работаю</h2>
            </div>
            <Link to="/services" style={{ textDecoration: 'none' }}>
              <button className="btn-text">
                Все услуги <ArrowRight size={16} style={{ display: 'inline', marginLeft: '8px' }} />
              </button>
            </Link>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 'var(--space-xl)',
            maxWidth: '1100px',
            margin: '0 auto'
          }}
          className="services-grid">
            {services.map((service) => (
              <Link
                key={service.id}
                to={`/services/${service.slug}`}
                style={{ textDecoration: 'none' }}
              >
                <div style={{
                  backgroundColor: 'var(--white)',
                  border: '1px solid var(--soft-gray)',
                  overflow: 'hidden',
                  height: '100%',
                  transition: 'all 400ms cubic-bezier(0.4, 0, 0.2, 1)',
                  cursor: 'pointer'
                }}
                className="service-card">
                  <div style={{
                    height: '320px',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    <img
                      src={service.image_url}
                      alt={service.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 600ms cubic-bezier(0.4, 0, 0.2, 1)',
                        filter: 'grayscale(20%)'
                      }}
                      className="service-card-image"
                    />
                  </div>
                  <div style={{
                    padding: 'var(--space-xl)'
                  }}>
                    <h3 style={{
                      marginBottom: 'var(--space-m)',
                      color: 'var(--black)',
                      fontSize: '1.75rem'
                    }}>
                      {service.title}
                    </h3>
                    <p style={{
                      color: 'var(--mid-gray)',
                      marginBottom: 'var(--space-l)',
                      fontSize: '0.9375rem',
                      lineHeight: '1.7'
                    }}>
                      {service.short_description}
                    </p>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      paddingTop: 'var(--space-l)',
                      borderTop: '1px solid var(--soft-gray)'
                    }}>
                      <span style={{
                        fontFamily: 'var(--font-display)',
                        fontSize: '1.125rem',
                        color: 'var(--charcoal)',
                        fontWeight: 400
                      }}>
                        {service.price}
                      </span>
                      <ArrowRight size={20} style={{ color: 'var(--charcoal)' }} className="service-arrow" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Transformation Stories - Gallery Style */}
      <section className="section">
        <div className="container">
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            marginBottom: 'var(--space-xxxl)'
          }}
          className="section-header">
            <div>
              <p className="caption" style={{ color: 'var(--mid-gray)', marginBottom: 'var(--space-m)' }}>
                Реальные истории
              </p>
              <h2 style={{ color: 'var(--black)' }}>Трансформации</h2>
            </div>
            <Link to="/cases" style={{ textDecoration: 'none' }}>
              <button className="btn-text">
                Все кейсы <ArrowRight size={16} style={{ display: 'inline', marginLeft: '8px' }} />
              </button>
            </Link>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 'var(--space-l)'
          }}
          className="cases-gallery">
            {caseStudies.slice(0, 3).map((caseStudy) => (
              <Link
                key={caseStudy.id}
                to={`/cases/${caseStudy.id}`}
                style={{ textDecoration: 'none' }}
              >
                <div style={{
                  height: '100%',
                  transition: 'transform 400ms cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                className="case-card">
                  <div style={{
                    height: '480px',
                    marginBottom: 'var(--space-l)',
                    overflow: 'hidden',
                    border: '1px solid var(--soft-gray)',
                    position: 'relative'
                  }}>
                    <img
                      src={caseStudy.after_image_url}
                      alt={`${caseStudy.client_name} после`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 600ms cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                      className="case-image"
                    />
                    <div style={{
                      position: 'absolute',
                      bottom: 'var(--space-l)',
                      right: 'var(--space-l)',
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      padding: 'var(--space-s) var(--space-m)',
                      backdropFilter: 'blur(10px)'
                    }}>
                      <p className="caption" style={{ color: 'var(--charcoal)' }}>После</p>
                    </div>
                  </div>
                  <h3 style={{
                    fontSize: '1.25rem',
                    marginBottom: 'var(--space-s)',
                    color: 'var(--black)'
                  }}>
                    {caseStudy.client_name}, {caseStudy.age}
                  </h3>
                  <p style={{
                    color: 'var(--mid-gray)',
                    fontSize: '0.875rem',
                    lineHeight: '1.6'
                  }}>
                    {caseStudy.challenge.substring(0, 100)}...
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* About - Split Section */}
      <section className="section" style={{ backgroundColor: 'var(--cream)' }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 'var(--space-xxxl)',
            alignItems: 'center'
          }}
          className="about-grid">
            <div style={{
              height: '600px',
              overflow: 'hidden',
              border: '1px solid var(--soft-gray)'
            }}>
              <img
                src={aboutStylist?.image_url}
                alt={aboutStylist?.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </div>
            <div>
              <p className="caption" style={{
                color: 'var(--mid-gray)',
                marginBottom: 'var(--space-m)'
              }}>
                О стилисте
              </p>
              <h2 style={{
                marginBottom: 'var(--space-xl)',
                color: 'var(--black)',
                fontSize: 'clamp(2rem, 5vw, 3.5rem)'
              }}>
                {aboutStylist?.name}
              </h2>
              <div style={{
                width: '60px',
                height: '2px',
                backgroundColor: 'var(--accent-gold)',
                marginBottom: 'var(--space-xl)'
              }} />
              <p className="large-text" style={{
                color: 'var(--charcoal)',
                marginBottom: 'var(--space-xl)',
                lineHeight: '1.8'
              }}>
                {aboutStylist?.description}
              </p>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 'var(--space-l)',
                marginBottom: 'var(--space-xl)'
              }}>
                <div style={{
                  padding: 'var(--space-l)',
                  backgroundColor: 'var(--white)',
                  border: '1px solid var(--soft-gray)'
                }}>
                  <p className="caption" style={{ color: 'var(--mid-gray)', marginBottom: 'var(--space-s)' }}>
                    Опыт работы
                  </p>
                  <p style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: '2.5rem',
                    color: 'var(--black)',
                    fontWeight: 300
                  }}>
                    {aboutStylist?.experience}
                  </p>
                </div>
                <div style={{
                  padding: 'var(--space-l)',
                  backgroundColor: 'var(--white)',
                  border: '1px solid var(--soft-gray)'
                }}>
                  <p className="caption" style={{ color: 'var(--mid-gray)', marginBottom: 'var(--space-s)' }}>
                    Город
                  </p>
                  <p style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: '1.75rem',
                    color: 'var(--black)',
                    fontWeight: 300
                  }}>
                    {aboutStylist?.city}
                  </p>
                </div>
              </div>
              <Link to="/about">
                <button className="btn-text">
                  Узнать больше <ArrowRight size={16} style={{ display: 'inline', marginLeft: '8px' }} />
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section">
        <div className="container">
          <div style={{
            maxWidth: '700px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            <p className="caption" style={{
              color: 'var(--mid-gray)',
              marginBottom: 'var(--space-m)'
            }}>
              Свяжитесь со мной
            </p>
            <h2 style={{ marginBottom: 'var(--space-xl)', color: 'var(--black)' }}>
              Начнём работу?
            </h2>
            <p className="large-text" style={{
              color: 'var(--charcoal)',
              marginBottom: 'var(--space-xxl)'
            }}>
              Отвечу в течение часа. Обсудим вашу задачу и назначим встречу
            </p>
            <ContactForm />
            <div style={{
              marginTop: 'var(--space-xxl)',
              paddingTop: 'var(--space-xxl)',
              borderTop: '1px solid var(--soft-gray)'
            }}>
              <p style={{
                color: 'var(--mid-gray)',
                marginBottom: 'var(--space-xl)',
                fontSize: '0.875rem'
              }}>
                Или напишите мне напрямую
              </p>
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 'var(--space-m)',
                flexWrap: 'wrap'
              }}>
                <a href="https://t.me/natali_ro" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                  <button className="btn-secondary" style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-m)'
                  }}>
                    <Send size={16} strokeWidth={1.5} />
                    Telegram
                  </button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.33%); }
        }

        .service-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.08);
        }

        .service-card:hover .service-card-image {
          transform: scale(1.05);
          filter: grayscale(0%);
        }

        .service-card:hover .service-arrow {
          transform: translateX(4px);
        }

        .service-arrow {
          transition: transform 300ms ease;
        }

        .case-card:hover {
          transform: translateY(-8px);
        }

        .case-card:hover .case-image {
          transform: scale(1.05);
        }

        .parallax-image:hover {
          transform: scale(1.02);
        }

        @media (max-width: 968px) {
          .hero-grid,
          .services-grid,
          .about-grid {
            grid-template-columns: 1fr !important;
          }

          .cases-gallery {
            grid-template-columns: 1fr !important;
          }

          .hero-image {
            height: 60vh !important;
            min-height: 400px !important;
          }

          .section-header {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: var(--space-l);
          }
        }
      `}</style>
    </div>
  );
};

export default Home;
